import React, { useState, useRef, useEffect } from "react";
import type { AssessmentItem } from "../../../../api/serving";
import { chassis } from "../shared/chassis";
import { useAudioCapture } from "../shared/useAudioCapture";
import { peT } from "../../../../i18n/peLesson";

export interface ItemAnswer {
  choiceId?: string;
  text?: string;
  audio?: Blob | null;
}

interface Props {
  item: AssessmentItem;
  value: ItemAnswer;
  onChange: (a: ItemAnswer) => void;
  disabled: boolean;
}

const choiceButton = (active: boolean): React.CSSProperties => ({
  borderColor: active ? chassis.accent : "rgb(209 213 219)",
  backgroundColor: active ? chassis.accent : "#fff",
  color: active ? chassis.accentText : chassis.textPrimary,
});

// --- mc + audio_match share choice rendering --------------------------------

// Speak a choice/clip on tap (WS2 Task 2). new Audio() avoids per-choice refs;
// the rejection from autoplay policy is harmless (the tap IS the user gesture).
const speak = (url?: string | null) => {
  if (!url) return;
  try {
    void new Audio(url).play().catch(() => {});
  } catch {
    /* no-op */
  }
};

const ChoiceList: React.FC<Props & { withAudio: boolean }> = ({
  item,
  value,
  onChange,
  disabled,
  withAudio,
}) => (
  <div className="space-y-2">
    {(item.choices ?? []).map((c) => {
      const active = value.choiceId === c.id;
      return (
        <button
          key={c.id}
          type="button"
          disabled={disabled}
          // WS2 Task 2: tapping a choice selects it AND speaks it (T1/T2).
          onClick={() => {
            onChange({ choiceId: c.id });
            if (withAudio) speak(c.audio_url);
          }}
          className="w-full text-left border rounded-md px-4 py-3 flex items-center gap-3 transition-colors disabled:opacity-60"
          style={choiceButton(active)}
        >
          <span className="font-semibold">{c.id}</span>
          {withAudio && c.audio_url && (
            <span aria-hidden className="text-2xl opacity-80">
              ▶︎
            </span>
          )}
          {/* T1 "tap the picture": render the comic still; fall back to text. */}
          {c.image_url ? (
            <img
              src={c.image_url}
              alt=""
              className="w-full rounded-md object-contain max-h-64"
              loading="lazy"
            />
          ) : (
            <span>{c.text}</span>
          )}
        </button>
      );
    })}
  </div>
);

// --- sentence_builder -------------------------------------------------------

const SentenceBuilder: React.FC<Props> = ({ item, onChange, disabled }) => {
  const t = peT(item.language);
  const [used, setUsed] = useState<string[]>([]);
  const bank = item.word_bank ?? [];

  const setSentence = (words: string[]) => {
    setUsed(words);
    onChange({ text: words.join(" ") });
  };

  return (
    <div>
      <div
        className="min-h-[3rem] border rounded-md p-3 mb-3 flex flex-wrap gap-2"
        style={{ borderColor: "rgb(209 213 219)", background: "#fff" }}
      >
        {used.length === 0 && (
          <span className="text-sm" style={{ color: chassis.textSecondary }}>
            {t.buildSentenceHint}
          </span>
        )}
        {used.map((w, i) => (
          <button
            key={`${w}-${i}`}
            type="button"
            disabled={disabled}
            onClick={() => setSentence(used.filter((_, idx) => idx !== i))}
            className="px-2 py-1 rounded text-sm font-medium disabled:opacity-60"
            style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
          >
            {w} ✕
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {bank.map((w, i) => {
          const taken = used.includes(w);
          return (
            <button
              key={`${w}-${i}`}
              type="button"
              disabled={disabled || taken}
              onClick={() => setSentence([...used, w])}
              className="px-3 py-1.5 rounded border text-sm disabled:opacity-40"
              style={{ borderColor: chassis.accent, color: chassis.accent }}
            >
              {w}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- open_response ----------------------------------------------------------

const OpenResponse: React.FC<Props> = ({ item, value, onChange, disabled }) => (
  <textarea
    rows={5}
    disabled={disabled}
    value={value.text ?? ""}
    onChange={(e) => onChange({ text: e.target.value })}
    placeholder={peT(item.language).writeAnswer}
    className="w-full border rounded-md p-3 focus:outline-none focus:ring-2 disabled:opacity-60"
    style={{ borderColor: "rgb(209 213 219)", color: chassis.textPrimary }}
  />
);

// --- audio capture ----------------------------------------------------------

const AudioCaptureItem: React.FC<Props> = ({ item, value, onChange, disabled }) => {
  const cap = useAudioCapture();
  const t = peT(item.language);

  // Surface the captured blob to the parent when it lands.
  React.useEffect(() => {
    if (cap.blob && cap.blob !== value.audio) onChange({ audio: cap.blob });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap.blob]);

  // WS2 Task 3: big mic button + clear recording state for non-reading students.
  const bigBtn: React.CSSProperties = {
    width: 96,
    height: 96,
    minWidth: 96,
    borderRadius: "9999px",
    fontSize: 40,
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {cap.state === "recording" ? (
          <button
            type="button"
            onClick={cap.stop}
            disabled={disabled}
            aria-label={t.stopRecording}
            className="text-white shadow-sm active:scale-95"
            style={{ ...bigBtn, backgroundColor: "#b91c1c" }}
          >
            ◼
          </button>
        ) : (
          <button
            type="button"
            onClick={cap.start}
            disabled={disabled}
            aria-label={cap.state === "recorded" ? t.recordAgain : t.recordAnswer}
            className="shadow-sm active:scale-95 disabled:opacity-60"
            style={{ ...bigBtn, backgroundColor: chassis.accent, color: chassis.accentText }}
          >
            🎤
          </button>
        )}
        <span className="text-base font-medium" style={{ color: chassis.textSecondary }}>
          {cap.state === "recording" ? (
            <span className="animate-pulse" style={{ color: "#b91c1c" }}>{t.recordingTapStop}</span>
          ) : cap.state === "recorded" ? (
            t.tapMicAgain
          ) : (
            t.tapMicSay
          )}
        </span>
      </div>
      {cap.audioUrl && (
        <audio controls src={cap.audioUrl} className="w-full">
          <track kind="captions" />
        </audio>
      )}
      {/* The browser's own error text is English; show a short localized line. */}
      {cap.error && (
        <p className="text-sm text-red-600" title={cap.error}>
          {t.micError}
        </p>
      )}
    </div>
  );
};

// --- drag_drop (tap-to-place matching/ordering) -----------------------------
// Interaction model (Fix B+):
//   1. Tap a statement in the tray to arm it ("Placing: X" strip appears).
//   2. Tap a bucket (or press Enter/Space on it) to place the armed statement.
//   3. Placed statements live NESTED inside their bucket as × chips.
//   4. Tap/activate a nested × chip to return it to the tray.
//   Buckets are always neutral containers — no persistent accent fill.
//   When a statement is armed, all buckets show a dashed accent outline.

const DragDropRenderer: React.FC<Props> = ({ item, value, onChange, disabled }) => {
  const t = peT(item.language);
  const items = item.drag_items ?? [];
  const targets = item.drag_targets ?? [];
  const isMatching = targets.some((t) => !t.startsWith("Position "));

  // SORT: repeated category labels → reusable buckets (fewer unique targets than items).
  // MATCH: unique targets === item count → single-use targets.
  // ORDERING: "Position N" targets → sequence slots.
  const categories = Array.from(new Set(targets));
  const isSort = isMatching && categories.length < items.length;

  // placements[stmtLabel] = targetLabel. Shape unchanged — backend reads this.
  const [placements, setPlacements] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const announceRef = useRef<HTMLDivElement>(null);

  // Spoken names (Run E2 J5): a picture tile is "Picture N", never its asset
  // key; an order slot is "Position N" in the item language, not the raw target.
  const itemName = (itm: string) =>
    item.drag_item_images?.[items.indexOf(itm)] ? t.picture(items.indexOf(itm) + 1) : itm;
  const targetName = (tgt: string) =>
    isMatching ? tgt : t.position(targets.indexOf(tgt) + 1);

  const announce = (msg: string) => {
    if (announceRef.current) {
      announceRef.current.textContent = "";
      // flush then set so screen readers re-announce identical messages
      requestAnimationFrame(() => {
        if (announceRef.current) announceRef.current.textContent = msg;
      });
    }
  };

  // Derive placements from value.text in disabled/result view.
  const currentPlacements = disabled && value.text
    ? (() => {
        try {
          const arr: string[] = JSON.parse(value.text);
          const p: Record<string, string> = {};
          arr.forEach((entry) => {
            if (isMatching) {
              const [it, tgt] = entry.split("→").map((s) => s.trim());
              if (it) p[it] = tgt ?? "";
            } else {
              p[entry] = "";
            }
          });
          return p;
        } catch { return {}; }
      })()
    : placements;

  const place = (itm: string, tgt: string) => {
    setPlacements((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k === itm || (!isSort && next[k] === tgt)) delete next[k];
      });
      next[itm] = tgt;
      if (Object.keys(next).length === items.length) {
        const answer = isMatching
          ? items.map((it) => `${it} → ${next[it] ?? ""}`)
          : [...items].sort(
              (a, b) => targets.indexOf(next[a]) - targets.indexOf(next[b]),
            );
        onChange({ text: JSON.stringify(answer) });
      } else {
        onChange({ text: "" });
      }
      return next;
    });
    setSelected(null);
    announce(t.announcePlaced(itemName(itm), targetName(tgt)));
  };

  const unplace = (itm: string) => {
    if (disabled) return;
    setPlacements((prev) => {
      const next = { ...prev };
      delete next[itm];
      return next;
    });
    onChange({ text: "" });
    announce(t.announceReturned(itemName(itm)));
  };

  const armStatement = (itm: string) => {
    const next = selected === itm ? null : itm;
    setSelected(next);
    if (next) announce(t.announcePlacing(itemName(next)));
  };

  const activateKey = (e: React.KeyboardEvent, fn: () => void) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); }
  };

  // Tray shows only unplaced statements.
  const unplacedItems = items.filter((itm) => !currentPlacements[itm]);
  const bucketList = isSort ? categories : targets;
  const isArmed = selected !== null;

  return (
    <div className="space-y-4">
      {/* aria-live region — invisible, for screen reader announcements */}
      <div
        ref={announceRef}
        aria-live="polite"
        aria-atomic="true"
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}
      />

      {/* Statement tray — only unplaced items */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: chassis.textSecondary }}>
          {isSort ? t.trayStatements : isMatching ? t.trayPeople : t.trayItems}
        </p>
        {unplacedItems.length === 0 && !disabled ? (
          <p className="text-xs" style={{ color: chassis.textSecondary }}>{t.allPlaced}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {unplacedItems.map((itm, idx) => {
              const isActive = selected === itm;
              const imgUrl = item.drag_item_images?.[items.indexOf(itm)] ?? null;
              return (
                <button
                  key={itm}
                  type="button"
                  disabled={disabled}
                  tabIndex={0}
                  aria-pressed={isActive}
                  onClick={() => armStatement(itm)}
                  onKeyDown={(e) => activateKey(e, () => armStatement(itm))}
                  style={{
                    border: `2px solid ${isActive ? chassis.accent : "rgb(209 213 219)"}`,
                    backgroundColor: isActive ? chassis.accent : "#fff",
                    color: isActive ? chassis.accentText : chassis.textPrimary,
                    borderRadius: "0.375rem",
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: disabled ? "default" : "pointer",
                    opacity: disabled ? 0.7 : 1,
                  }}
                >
                  {imgUrl ? (
                    <span className="flex items-center gap-2">
                      <img src={imgUrl} alt="" className="h-24 w-24 object-contain rounded" loading="lazy" />
                    </span>
                  ) : itm}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* "Now placing" strip — visible when a statement is armed */}
      {isArmed && !disabled && (
        <div
          className="flex items-center gap-3 rounded-md px-3 py-2"
          style={{ backgroundColor: "rgb(239 246 255)", border: `1px solid ${chassis.accent}` }}
        >
          <span className="text-sm" style={{ color: chassis.textPrimary }}>
            {t.placing} <strong>{selected ? itemName(selected) : ""}</strong>
          </span>
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="ml-auto text-xs font-semibold"
            style={{ color: chassis.accent, cursor: "pointer" }}
          >
            {t.cancel}
          </button>
        </div>
      )}

      {/* Bucket / target area */}
      {(isSort || isArmed || Object.keys(currentPlacements).length > 0 || disabled) && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: chassis.textSecondary }}>
              {isSort ? t.bucketCategories : isMatching ? t.bucketTreasures : t.bucketOrder}
            </p>
            {!isMatching && (
              <span className="text-xs flex items-center gap-1" style={{ color: chassis.textSecondary }} aria-hidden>
                1 <span style={{ fontSize: "0.9rem" }}>→</span> {items.length}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {bucketList.map((tgt, ti) => {
              const placedHere = items.filter((itm) => currentPlacements[itm] === tgt);
              const bucketLabel = isMatching ? tgt : t.position(ti + 1);
              const displayLabel = isMatching ? tgt : String(ti + 1);
              return (
                <div
                  key={tgt}
                  role="group"
                  aria-label={t.bucketAria(bucketLabel, placedHere.length)}
                  tabIndex={isArmed && !disabled ? 0 : -1}
                  onClick={() => { if (isArmed && !disabled) place(selected!, tgt); }}
                  onKeyDown={(e) => activateKey(e, () => { if (isArmed && !disabled) place(selected!, tgt); })}
                  className="rounded-lg transition-colors"
                  style={{
                    border: `2px ${isArmed && !disabled ? "dashed" : "solid"} ${isArmed && !disabled ? chassis.accent : "rgb(209 213 219)"}`,
                    backgroundColor: "#fff",
                    padding: "0.625rem 0.75rem",
                    minWidth: isMatching ? "9rem" : "5rem",
                    cursor: isArmed && !disabled ? "pointer" : "default",
                  }}
                >
                  {/* Bucket label */}
                  <p
                    className="font-semibold mb-1"
                    style={{
                      fontSize: isMatching ? "0.875rem" : "1.875rem",
                      lineHeight: isMatching ? undefined : 1,
                      color: chassis.textPrimary,
                      textAlign: isMatching ? undefined : "center",
                    }}
                  >
                    {displayLabel}
                  </p>
                  {/* Nested placed-statement chips */}
                  {placedHere.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {placedHere.map((stmt) => (
                        <button
                          key={stmt}
                          type="button"
                          disabled={disabled}
                          tabIndex={0}
                          aria-label={t.chipAria(itemName(stmt), bucketLabel)}
                          onClick={(e) => { e.stopPropagation(); unplace(stmt); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); unplace(stmt); } }}
                          className="flex items-center gap-1 rounded"
                          style={{
                            border: "1px solid rgb(209 213 219)",
                            backgroundColor: "rgb(240 253 244)",
                            padding: "0.2rem 0.45rem",
                            fontSize: "0.75rem",
                            color: chassis.textPrimary,
                            cursor: disabled ? "default" : "pointer",
                          }}
                        >
                          {/* Image-drag: render the comic still in the placed slot too,
                              matching the tray thumbnail (D1). Text items fall back to label. */}
                          {(() => {
                            const placedImg = item.drag_item_images?.[items.indexOf(stmt)] ?? null;
                            return placedImg ? (
                              <img src={placedImg} alt="" className="h-16 w-16 object-contain rounded" loading="lazy" />
                            ) : (
                              <span>{stmt}</span>
                            );
                          })()}
                          {!disabled && <span aria-hidden style={{ fontWeight: 700, marginLeft: 2 }}>×</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom hint — only when nothing is armed and tray still has items */}
      {!isArmed && unplacedItems.length > 0 && !disabled && (
        <p className="text-xs" style={{ color: chassis.textSecondary }}>
          {isSort
            ? t.hintSort
            : isMatching
            ? t.hintMatch
            : t.hintOrder}
        </p>
      )}
    </div>
  );
};

// audio_match (response_mode="audio") now uses real mic capture via
// AudioCaptureItem (WS2 Task 3). The "I said it" stub was removed here.

// --- prompt audio (WS2 Task 1) ----------------------------------------------
// Non-reading kindergartners hear the question read aloud. Auto-plays on item
// load (best-effort — browsers may block autoplay-with-sound until a gesture),
// with a large, obvious replay button as the guaranteed fallback. EN/ES follow
// the launch language because the served audio URL already matches it.

const AUTO_REPLAY_MS = 10000; // amendment: re-read the prompt once if idle ~10s

const PromptAudio: React.FC<{ url: string; itemKey: string; interacted: boolean; lang: string }> = ({
  url,
  itemKey,
  interacted,
  lang,
}) => {
  const t = peT(lang);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  // Latest interaction state, read inside timers without re-subscribing.
  const interactedRef = useRef(interacted);
  interactedRef.current = interacted;
  const autoReplayedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const playFromStart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    void el.play().catch(() => {});
  };

  // Autoplay whenever the item changes; reset the one-shot auto-replay guard.
  useEffect(() => {
    autoReplayedRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    playFromStart();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [itemKey]);

  // Amendment: when the prompt finishes, if the student hasn't interacted
  // within ~10s, replay it ONCE to re-engage. Manual replay also resets nothing
  // (still one auto-replay per item).
  const onEnded = () => {
    setPlaying(false);
    if (autoReplayedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!interactedRef.current && !autoReplayedRef.current) {
        autoReplayedRef.current = true;
        playFromStart();
      }
    }, AUTO_REPLAY_MS);
  };

  const replay = () => {
    autoReplayedRef.current = true; // a manual tap counts; no further auto-replay
    if (timerRef.current) clearTimeout(timerRef.current);
    playFromStart();
  };

  return (
    <div className="flex items-center gap-3 mb-4">
      <button
        type="button"
        onClick={replay}
        aria-label={t.playQuestionAgain}
        className="flex items-center justify-center rounded-full shadow-sm transition-transform active:scale-95"
        style={{
          width: 72,
          height: 72,
          minWidth: 72,
          backgroundColor: chassis.accent,
          color: chassis.accentText,
          fontSize: 34,
          lineHeight: 1,
        }}
      >
        {playing ? "🔊" : "▶︎"}
      </button>
      <span className="text-sm" style={{ color: chassis.textSecondary }}>
        {t.tapToHear}
      </span>
      <audio
        ref={audioRef}
        src={url}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onEnded={onEnded}
        onPause={() => setPlaying(false)}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
};

// --- dispatcher -------------------------------------------------------------

const renderByType = (props: Props): React.ReactNode => {
  const t1t2 = props.item.tier === "T1" || props.item.tier === "T2";
  switch (props.item.type) {
    case "mc":
      // WS2 Task 2: at T1/T2 each choice speaks on tap (audio_url from serving).
      return <ChoiceList {...props} withAudio={t1t2} />;
    case "audio_match":
      // Live API: response_mode="audio" → oral response. WS2 Task 3: real mic
      // capture (stub retired). Mock: response_mode="auto" → choice-based.
      if (props.item.response_mode === "audio") return <AudioCaptureItem {...props} />;
      return <ChoiceList {...props} withAudio />;
    case "drag_drop":
      return <DragDropRenderer {...props} />;
    case "sentence_builder":
      return <SentenceBuilder {...props} />;
    case "open_response":
      return <OpenResponse {...props} />;
    case "audio":
      return <AudioCaptureItem {...props} />;
    default:
      return null;
  }
};

const FormativeItem: React.FC<Props> = (props) => {
  const v = props.value ?? {};
  const interacted = !!(v.choiceId || v.text || v.audio);
  return (
    <div>
      {props.item.audio_prompt_url && (
        <PromptAudio
          url={props.item.audio_prompt_url}
          itemKey={props.item.item_id}
          interacted={interacted}
          lang={props.item.language}
        />
      )}
      {renderByType(props)}
    </div>
  );
};

export default FormativeItem;
